import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';

import { endpoints } from '../endpoints';
import { CategoryKey, IndustryKey, IndustryNycKey, TRANSLATIONS, USER_FORM_LISTS } from '../../constants/userFormLists';
import { useCurrentLanguage } from '../../utils/hooks';
import { RootState } from '../../store/store';

interface FiltersResponse {
  categories: CategoryKey[];
  industries: IndustryKey[] | IndustryNycKey[];
}

const useFetchSearchFilters = () => {
  const currentLanguage = useCurrentLanguage();
  const useNycIntake = useSelector((state: RootState) => state.config.useNycIntake);

  const [rawFilters, setRawFilters] = useState<FiltersResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const response = await axios.get<FiltersResponse>(endpoints.getSearchFilters);
        setRawFilters(response.data);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFilters();
  }, []);

  const filters = useMemo(() => {
    if (!rawFilters) return null;

    if (useNycIntake) {
      const categories = USER_FORM_LISTS.advisorCategories.map(item => ({
        label: TRANSLATIONS.advisorCategories[currentLanguage][item as keyof typeof TRANSLATIONS.advisorCategories.en],
        value: item,
      }));

      const industries = USER_FORM_LISTS.currentIndustry.map(item => ({
        label: TRANSLATIONS.currentIndustry[currentLanguage][item as keyof typeof TRANSLATIONS.currentIndustry.en],
        value: item,
      }));

      return { categories, industries };
    }

    const categories = rawFilters.categories.map(item => ({
      label: TRANSLATIONS.categories[currentLanguage][item],
      value: item,
    }));

    const industries = USER_FORM_LISTS.industries_nyc.map(item => ({
      label: TRANSLATIONS.industries_nyc[currentLanguage][item as IndustryNycKey],
      value: item,
    }));

    return { categories, industries };
  }, [rawFilters, currentLanguage, useNycIntake]);

  return {
    categories: filters?.categories || [],
    industries: filters?.industries || [],
    isLoading,
  };
};

export default useFetchSearchFilters;
